/**
 * Department Edit Page
 * Page for editing existing departments
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Link,
} from '@mui/material';
import { useSnackbar } from 'notistack';

// Components
import { DepartmentForm } from './components';
import { PageShell } from '../../components';

// Services
import { departmentService } from '../../services/departmentService';

// Types
import { Department, DepartmentFormData, UpdateDepartmentRequest, SafetyCharacteristicType } from '../../types/department';

const DepartmentEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();

  const [department, setDepartment] = useState<Department | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get return URL params
  const companyId = searchParams.get('companyId');

  // Load department data
  const loadDepartment = async () => {
    if (!id) {
      setError('ID do setor não fornecido');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const departmentData = await departmentService.getDepartmentById(id);
      setDepartment(departmentData);
    } catch (err: any) {
      console.error('Error loading department:', err);
      const errorMessage = (err.response?.data?.message || err.message) ?? 'Erro ao carregar dados do setor';
      setError(errorMessage);
      enqueueSnackbar('Erro ao carregar dados do setor', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadDepartment();
  }, [id]);

  // Handle form submission
  const handleSubmit = async (data: DepartmentFormData) => {
    if (!id) return;

    setSaving(true);
    
    try {
      console.log('🔄 Updating department:', { id, data });
      
      // Convert safety characteristics object to array
      const characteristicsArray = Object.entries(data.safetyCharacteristics)
        .filter(([_, enabled]) => enabled)
        .map(([characteristic]) => characteristic as SafetyCharacteristicType);
      
      const requestData: UpdateDepartmentRequest = {
        name: data.name,
        unitId: data.unitId,
        companyId: data.companyId,
        
        location: data.location ?? undefined,
        construction: data.construction ?? undefined,
        ceilingHeight: data.ceilingHeight ?? undefined,
        roofing: data.roofing ?? undefined,
        ventilation: data.ventilation ?? undefined,
        lighting: data.lighting ?? undefined,
        
        safetyCharacteristics: characteristicsArray,
      };
      
      console.log('📊 Safety characteristics array:', characteristicsArray);
      
      await departmentService.updateDepartment(id, requestData);
      
      console.log('✅ Department updated successfully');
      enqueueSnackbar('Setor atualizado com sucesso!', { variant: 'success' });
      
      // Navigate back with preserved company filter if it exists
      if (companyId) {
        navigate(`/departments?companyId=${companyId}`);
      } else {
        navigate('/departments');
      }
    } catch (error: any) {
      console.error('❌ Error updating department:', error);
      const errorMessage = (error.response?.data?.message || error.message) ?? 'Erro ao atualizar setor';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (companyId) {
      navigate(`/departments?companyId=${companyId}`);
    } else {
      navigate('/departments');
    }
  };

  const breadcrumbs = [
    { label: 'Início', path: '/' },
    { label: 'Setores', path: companyId ? `/departments?companyId=${companyId}` : '/departments' },
    { label: 'Editar Setor' },
  ];

  // Loading state
  if (loading) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Editar Setor">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Carregando dados do setor...
          </Typography>
        </Box>
      </PageShell>
    );
  }

  // Error state
  if (error) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Editar Setor">
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Box textAlign="center">
          <Typography variant="h6" gutterBottom>
            Não foi possível carregar os dados do setor
          </Typography>
          <Link
            href="/departments"
            onClick={(e) => {
              e.preventDefault();
              navigate('/departments');
            }}
          >
            Voltar para a lista de setores
          </Link>
        </Box>
      </PageShell>
    );
  }

  // No department found
  if (!department) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Editar Setor">
        <Alert severity="warning" sx={{ mb: 3 }}>
          Setor não encontrado
        </Alert>
        <Box textAlign="center">
          <Typography variant="h6" gutterBottom>
            O setor solicitado não foi encontrado
          </Typography>
          <Link
            href="/departments"
            onClick={(e) => {
              e.preventDefault();
              navigate('/departments');
            }}
          >
            Voltar para a lista de setores
          </Link>
        </Box>
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs={breadcrumbs}
      title={`Editar Setor: ${department.name}`}
      subtitle="Atualize as informações do setor"
    >
      {department.company && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: -2, mb: 3 }}>
          Empresa: {department.company.tradeName || department.company.legalName}
        </Typography>
      )}

      {/* Form Card */}
      <Card>
        <CardContent>
          <DepartmentForm
            mode="edit"
            department={department}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={saving}
          />
        </CardContent>
      </Card>
    </PageShell>
  );
};

export default DepartmentEditPage;